import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../components/Layout.jsx';
import { Alert, Card, Empty, ErrorState, Input, Loading, Select, Textarea } from '../components/ui.jsx';
import { Modal, ConfirmModal } from '../components/Modal.jsx';
import { IconArchive, IconPlus } from '../components/Icons.jsx';
import { api } from '../lib/api.js';
import { useApi } from '../lib/hooks.js';
import { useAuth } from '../lib/auth.jsx';
import { useToast } from '../lib/toast.jsx';
import { roleLabel, t } from '../lib/i18n.js';
import { formatDateTime } from '../lib/format.js';

export default function Settings() {
  const { user, isAdmin, settings, setSettings } = useAuth();
  const toast = useToast();

  return (
    <>
      <PageHeader
        title={t.settings.title}
        subtitle={`${t.auth.signedInAs}: ${user?.full_name || user?.username}`}
      />
      <div className="page">
        {isAdmin && <FacilityCard settings={settings} setSettings={setSettings} toast={toast} />}
        <PasswordCard toast={toast} />
        {isAdmin && <UsersCard toast={toast} currentUserId={user?.id} />}
        {isAdmin && <BackupCard />}
        <InstallCard />
      </div>
    </>
  );
}

function FacilityCard({ settings, setSettings, toast }) {
  const [form, setForm] = useState(settings);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => setForm(settings), [settings]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const saved = await api.put('/api/settings', {
        facility_name: form.facility_name || '',
        facility_phone: form.facility_phone || '',
        facility_address: form.facility_address || '',
        receipt_footer: form.receipt_footer || '',
        whatsapp_country_code: form.whatsapp_country_code || '',
      });
      setSettings(saved);
      toast.success(t.settings.detailsSaved);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      title={t.settings.facilityDetails}
      footer={<span className="small muted">{t.settings.facilityFooterNote}</span>}
    >
      {error && <Alert tone="error">{error}</Alert>}
      <Input
        label={t.settings.facilityName}
        value={form.facility_name || ''}
        onChange={(e) => set({ facility_name: e.target.value })}
      />
      <Input
        label={t.common.phone}
        value={form.facility_phone || ''}
        onChange={(e) => set({ facility_phone: e.target.value })}
        dir="ltr"
      />
      <Input
        label={t.common.address}
        value={form.facility_address || ''}
        onChange={(e) => set({ facility_address: e.target.value })}
      />
      <Textarea
        label={t.settings.receiptFooter}
        rows={2}
        value={form.receipt_footer || ''}
        onChange={(e) => set({ receipt_footer: e.target.value })}
      />
      <Input
        label={t.settings.whatsappCode}
        value={form.whatsapp_country_code || ''}
        onChange={(e) => set({ whatsapp_country_code: e.target.value.replace(/\D/g, '') })}
        hint={t.settings.whatsappCodeHint}
        inputMode="numeric"
        dir="ltr"
      />
      <button type="button" className="btn btn-primary" onClick={save} disabled={busy}>
        {busy ? <span className="spinner" /> : t.settings.saveDetails}
      </button>
    </Card>
  );
}

function PasswordCard({ toast }) {
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const mismatch = form.confirm.length > 0 && form.new_password !== form.confirm;

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/auth/change-password', {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      setForm({ current_password: '', new_password: '', confirm: '' });
      toast.success(t.auth.passwordChanged);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title={t.auth.changePassword}>
      {error && <Alert tone="error">{error}</Alert>}
      <Input
        label={t.auth.currentPassword}
        type="password"
        autoComplete="current-password"
        dir="ltr"
        value={form.current_password}
        onChange={(e) => set({ current_password: e.target.value })}
      />
      <Input
        label={t.auth.newPassword}
        type="password"
        autoComplete="new-password"
        dir="ltr"
        value={form.new_password}
        onChange={(e) => set({ new_password: e.target.value })}
        hint={t.auth.passwordMinimum}
      />
      <Input
        label={t.auth.confirmPassword}
        type="password"
        autoComplete="new-password"
        dir="ltr"
        value={form.confirm}
        onChange={(e) => set({ confirm: e.target.value })}
        error={mismatch ? t.auth.passwordsDoNotMatch : undefined}
      />
      <button
        type="button"
        className="btn btn-primary"
        onClick={save}
        disabled={busy || mismatch || form.new_password.length < 8 || !form.current_password}
      >
        {busy ? <span className="spinner" /> : t.auth.changePassword}
      </button>
    </Card>
  );
}

function UsersCard({ toast, currentUserId }) {
  const { data, error, loading, reload } = useApi('/api/users');
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);

  const remove = async () => {
    setBusy(true);
    try {
      await api.del(`/api/users/${deleting.id}`);
      toast.success(t.settings.userDeleted);
      setDeleting(null);
      reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      title={t.settings.users}
      actions={
        <button type="button" className="btn btn-sm btn-primary" onClick={() => setEditing({})}>
          <IconPlus /> {t.common.add}
        </button>
      }
    >
      <ErrorState error={error} onRetry={reload} />
      {loading && !data && <Loading />}
      {data && data.length === 0 && <Empty title={t.settings.noUsers} />}
      {data && data.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t.auth.username}</th>
                <th>{t.settings.fullName}</th>
                <th>{t.settings.role}</th>
                <th>{t.common.status}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.map((row) => (
                <tr key={row.id}>
                  <td dir="ltr">{row.username}</td>
                  <td>{row.full_name || '-'}</td>
                  <td>{roleLabel[row.role] || row.role}</td>
                  <td>
                    <span className={`badge ${row.active ? 'cash' : 'muted'}`}>
                      {row.active ? t.common.active : t.settings.disabled}
                    </span>
                  </td>
                  <td>
                    <div className="btn-row">
                      <button type="button" className="btn btn-sm btn-ghost" onClick={() => setEditing(row)}>
                        {t.common.edit}
                      </button>
                      {row.id !== currentUserId && (
                        <button type="button" className="btn btn-sm btn-danger" onClick={() => setDeleting(row)}>
                          {t.common.delete}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <UserForm
          userRecord={editing}
          currentUserId={currentUserId}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            toast.success(t.settings.userSaved);
            reload();
          }}
        />
      )}

      {deleting && (
        <ConfirmModal
          title={t.settings.deleteUser}
          message={t.settings.deleteUserMessage(deleting.username)}
          onConfirm={remove}
          onClose={() => setDeleting(null)}
          busy={busy}
        />
      )}
    </Card>
  );
}

function UserForm({ userRecord, currentUserId, onClose, onSaved }) {
  const isEdit = Boolean(userRecord.id);
  const [form, setForm] = useState({
    username: userRecord.username || '',
    full_name: userRecord.full_name || '',
    role: userRecord.role || 'staff',
    password: '',
    active: userRecord.active ?? true,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      if (isEdit) {
        const payload = { full_name: form.full_name, role: form.role, active: form.active };
        if (form.password) payload.password = form.password;
        await api.patch(`/api/users/${userRecord.id}`, payload);
      } else {
        await api.post('/api/users', form);
      }
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const passwordTooShort = form.password.length > 0 && form.password.length < 8;
  const canSave = isEdit ? !passwordTooShort : form.username.trim() && form.password.length >= 8;

  return (
    <Modal
      title={isEdit ? t.settings.editUser(userRecord.username) : t.settings.newUser}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            {t.common.cancel}
          </button>
          <button type="button" className="btn btn-primary" onClick={save} disabled={busy || !canSave}>
            {busy ? <span className="spinner" /> : t.common.save}
          </button>
        </>
      }
    >
      {error && <Alert tone="error">{error}</Alert>}
      {!isEdit && (
        <Input
          label={t.auth.username}
          required
          autoCapitalize="none"
          dir="ltr"
          value={form.username}
          onChange={(e) => set({ username: e.target.value })}
        />
      )}
      <Input label={t.settings.fullName} value={form.full_name} onChange={(e) => set({ full_name: e.target.value })} />
      <Select
        label={t.settings.role}
        value={form.role}
        onChange={(e) => set({ role: e.target.value })}
        options={[
          { value: 'staff', label: t.settings.roleStaff },
          { value: 'admin', label: t.settings.roleAdmin },
        ]}
      />
      <Input
        label={isEdit ? t.settings.newPasswordOptional : t.auth.password}
        type="password"
        autoComplete="new-password"
        dir="ltr"
        value={form.password}
        onChange={(e) => set({ password: e.target.value })}
        error={passwordTooShort ? t.auth.passwordMinimum : undefined}
      />
      {isEdit && userRecord.id !== currentUserId && (
        <Select
          label={t.common.status}
          value={form.active ? 'true' : 'false'}
          onChange={(e) => set({ active: e.target.value === 'true' })}
          options={[
            { value: 'true', label: t.common.active },
            { value: 'false', label: t.settings.disabled },
          ]}
        />
      )}
    </Modal>
  );
}

function BackupCard() {
  const { data } = useApi('/api/backup/summary');
  const last = data?.history?.[0];
  return (
    <Card title={t.backup.title}>
      <p className="small muted mt-0">
        {last
          ? t.backup.lastBackupLine(formatDateTime(last.created_at), last.created_by_name || t.common.system)
          : t.backup.noBackupYet}
      </p>
      {!last && <Alert tone="warn">{t.backup.noBackupWarning}</Alert>}
      <Link className="btn btn-primary" to="/backup">
        <IconArchive /> {t.backup.openBackup}
      </Link>
    </Card>
  );
}

/** Install prompt and service-worker state, so the app can be put on a home screen. */
function InstallCard() {
  const [prompt, setPrompt] = useState(null);
  const [installed, setInstalled] = useState(
    window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true,
  );

  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setPrompt(null);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  return (
    <Card title={t.settings.install}>
      {installed ? (
        <Alert tone="success">{t.settings.installed}</Alert>
      ) : prompt ? (
        <>
          <p className="small muted mt-0">{t.settings.installPrompt}</p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={async () => {
              prompt.prompt();
              await prompt.userChoice;
              setPrompt(null);
            }}
          >
            {t.settings.installButton}
          </button>
        </>
      ) : (
        <p className="small muted mt-0">{t.settings.installManual}</p>
      )}
    </Card>
  );
}
